import { defineDocument, em, link, list, p, section, strong, table, text, } from '../../schema';
export const report202603TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-03-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-03-top-party-games-and-streamers',
    route: '/party-central/reports/2026-03-top-party-games-and-streamers',
    seo: {
        title: 'March 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for March 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'March 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'blue',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-04-02T00:00:00.000Z'),
        updatedAt: new Date('2026-04-02T00:00:00.000Z'),
        reportMonth: '2026-03',
        dataRange: {
            start: '2026-03-01',
            end: '2026-03-31',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('We tracked more than 203,000 party-game streams from 81,412 streamers across 35 languages in March 2026. A few strong games continue to shape the party game landscape, and the biggest winners are the ones streamers keep coming back to again and again. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' for collection and scoring details.'),
                ]),
                p([
                    em('Diablo II: Resurrected'),
                    text(' stayed at #1 in Game Party Score, and March made the reason clearer than ever. It was not the widest-played game on Twitch, but it was the game people came back to most reliably at scale. '),
                    em('Words On Stream'),
                    text(" reinforced the same theme from the niche end of the market: if a party game becomes part of a creator's regular routine, it can punch far above its size."),
                ]),
                p([
                    text("February's breakout did not cool off: "),
                    em('Super Battle Golf'),
                    text(' followed its hot debut by taking the widest streamer base in March with nearly 10,000 unique streamers. '),
                    em('Fall Guys'),
                    text(' and '),
                    em('Mahjong Soul'),
                    text(" both climbed in this month's Party Score rankings to round out the top three games. March felt less like a month of one-off curiosity and more like a month in which a stronger top tier started to harden."),
                ]),
                p([
                    text('New releases still had real reasons for optimism. '),
                    em('Poker Night at the Inventory'),
                    text(' found instant traction with streamers right out of the gate, and '),
                    em('Ham: The Game'),
                    text(' also landed an encouraging first month. Once creators decide a new release plays well live, that momentum can spread quickly.'),
                ]),
                p([
                    link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                    text(' rose from #4 last month to #1 in the streamer Party Score rankings, while huge appearances from '),
                    link('https://twitch.tv/caseoh_', 'caseoh_'),
                    text(' and other variety stars kept showing how much attention party games can command in the right moment. English-language streams still dominated by volume, but Italian had the highest average viewers per stream by far.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            blocks: [
                p([
                    text('Party Score is a proprietary metric developed by '),
                    link('/', 'Airhead Games'),
                    text(' to rank party game performance on Twitch. '),
                    strong('Game Party Score'),
                    text(" is a 0-100 percentile-ranked composite that measures a title's overall streaming presence by combining streamer breadth, streamer dedication (stickiness), and audience draw. "),
                    strong('Streamer Party Score'),
                    text(' uses a parallel methodology applied at the creator level, weighting how consistently a streamer appears, how large an audience they bring, and how many different party games they cover. Full scoring details are in the '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text('.'),
                ]),
            ],
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            text("March kept February's main cast but changed the order. "),
                            em('Diablo II: Resurrected'),
                            text(' held #1, '),
                            em('Fall Guys'),
                            text(' rose from #4 to #2, '),
                            em('Mahjong Soul'),
                            text(' climbed from #6 to #3, and '),
                            em('Just Dance 2026 Edition'),
                            text(' and '),
                            em('Pinball'),
                            text(' each moved up four spots.'),
                        ]),
                        p([
                            text('The biggest absence is '),
                            em('Among Us'),
                            text('. It remained #2 by raw stream volume, yet fell out of the top 15 Party Score games because so much of its activity came from wide sampling rather than repeat play. Five new entrants — '),
                            em('Lycans'),
                            text(', '),
                            em('Gartic on Stream'),
                            text(', '),
                            em('Granblue Fantasy Versus: Rising'),
                            text(', '),
                            em('Casino'),
                            text(', and '),
                            em('Whatsamusic'),
                            text(' — show the other side of the story: small, committed communities matter just as much as large, fickle ones.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    'Diablo II: Resurrected',
                                    '94',
                                    '20,276',
                                    '3,114',
                                    '6.5',
                                    '18',
                                ],
                                ['2', 'Fall Guys', '92', '14,345', '6,764', '2.1', '43'],
                                ['3', 'Mahjong Soul', '92', '3,508', '1,235', '2.8', '29'],
                                ['4', 'Words On Stream', '92', '7,468', '626', '11.9', '16'],
                                [
                                    '5',
                                    'Just Dance 2026 Edition',
                                    '92',
                                    '444',
                                    '177',
                                    '2.5',
                                    '63',
                                ],
                                ['6', 'Pinball', '92', '709', '165', '4.3', '32'],
                                ['7', 'Lycans', '92', '312', '122', '2.6', '104'],
                                [
                                    '8',
                                    'Super Smash Bros. Ultimate',
                                    '91',
                                    '13,515',
                                    '3,813',
                                    '3.5',
                                    '16',
                                ],
                                ['9', 'Mario Kart 64', '91', '386', '129', '3.0', '40'],
                                ['10', 'Gartic on Stream', '91', '218', '39', '5.6', '57'],
                                [
                                    '11',
                                    'Mario Kart World',
                                    '90',
                                    '6,345',
                                    '2,475',
                                    '2.6',
                                    '19',
                                ],
                                [
                                    '12',
                                    'Jackbox Party Packs',
                                    '90',
                                    '4,202',
                                    '2,096',
                                    '2.0',
                                    '24',
                                ],
                                [
                                    '13',
                                    'Granblue Fantasy Versus: Rising',
                                    '90',
                                    '1,670',
                                    '659',
                                    '2.5',
                                    '19',
                                ],
                                ['14', 'Casino', '90', '120', '39', '3.1', '329'],
                                ['15', 'Whatsamusic', '89', '267', '115', '2.3', '29'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The streamer table turned over hard. '),
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' climbed from #4 to #1, '),
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(' moved from #3 to #2, and nine of the top 15 names were new to the leaderboard. March rewarded streamers who could balance regular appearances with either a real audience or coverage across multiple games.'),
                        ]),
                        p([
                            text('That churn cuts both ways. February leader '),
                            link('https://twitch.tv/gaules', 'Gaules'),
                            text(' dropped out of the top 15 entirely, while creators like '),
                            link('https://twitch.tv/ray', 'Ray'),
                            text(', '),
                            link('https://twitch.tv/mrllamasc', 'MrLlamaSC'),
                            text(', '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(', and '),
                            link('https://twitch.tv/emilycc', 'Emilycc'),
                            text(' arrived on very different strengths: some on reach, some on frequency, some on breadth.'),
                        ]),
                        p('We hypothesize streamer rankings will continue to be more volatile than game rankings for any given month because of the unpredictable nature of life for any one person. In contrast, game rankings stay steadier because they represent the combined habits of thousands of people, which naturally smooths out individual "noise."'),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '98',
                                    '18',
                                    '397',
                                    '5',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '37',
                                    '180',
                                    '4',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/ray', 'Ray')],
                                    '97',
                                    '11',
                                    '2,317',
                                    '8',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                    '97',
                                    '48',
                                    '933',
                                    '2',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/misterjday', 'MisterJDay')],
                                    '97',
                                    '12',
                                    '502',
                                    '4',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/hypnoshark', 'hypnoshark')],
                                    '97',
                                    '16',
                                    '397',
                                    '6',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '97',
                                    '13',
                                    '332',
                                    '6',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '97',
                                    '17',
                                    '203',
                                    '14',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/francesconigris', 'francesconigris'),
                                    ],
                                    '97',
                                    '18',
                                    '154',
                                    '5',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/logan_usa', 'logan_usa')],
                                    '97',
                                    '37',
                                    '116',
                                    '3',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/chilledchaos', 'ChilledChaos')],
                                    '96',
                                    '10',
                                    '2,908',
                                    '6',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/paache_', 'paache_')],
                                    '96',
                                    '19',
                                    '1,448',
                                    '2',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/gamescage', 'GamesCage')],
                                    '96',
                                    '11',
                                    '421',
                                    '3',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/eevisu', 'EEvisu')],
                                    '96',
                                    '26',
                                    '245',
                                    '2',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/emilycc', 'Emilycc')],
                                    '96',
                                    '110',
                                    '235',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            blocks: [
                p('The tables below rank games four ways: most streamed, biggest audiences, widest streamer base, and stickiest. Each ranking tells a different story about how a title earns attention.'),
            ],
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Diablo II: Resurrected'),
                            text(' still led comfortably, but the bigger story was the pack behind it. A clear second tier solidified with '),
                            em('Among Us'),
                            text(', '),
                            em('Fall Guys'),
                            text(', '),
                            em('Super Battle Golf'),
                            text(', and '),
                            em('Super Smash Bros. Ultimate'),
                            text(" all seen on stream between 13,000 and 15,000 times. The top nine games in this month's report were the same as in February's report, with only "),
                            em('Gartic Phone'),
                            text(' replacing '),
                            em('Party Animals'),
                            text(' in the tenth spot.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Diablo II: Resurrected', '20,276', '3,114', '18'],
                                ['2', 'Among Us', '14,983', '8,744', '28'],
                                ['3', 'Fall Guys', '14,345', '6,764', '43'],
                                ['4', 'Super Battle Golf', '14,132', '9,948', '48'],
                                ['5', 'Super Smash Bros. Ultimate', '13,515', '3,813', '16'],
                                ['6', 'Lethal Company', '8,285', '4,463', '10'],
                                ['7', 'Words On Stream', '7,468', '626', '16'],
                                ['8', 'Mario Kart World', '6,345', '2,475', '19'],
                                ['9', 'Sonic Racing: CrossWorlds', '6,067', '2,716', '8'],
                                ['10', 'Gartic Phone', '4,780', '2,748', '22'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('This table turned over heavily from February with '),
                            em('Casino'),
                            text(', '),
                            em('Maitre des Fleurs'),
                            text(', and '),
                            em('The Game of Life'),
                            text(' being the only holdovers. '),
                            em('Jinsei Game for Nintendo Switch'),
                            text(' built its audience from a small group of Chinese-language creators who played it together in a handful of high-performing collab streams. Rather than breaking out broadly across Twitch, it looks like the game caught on inside one creator circle that was especially good at turning it into an event.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Jinsei Game for Nintendo Switch', '915', '17', '12'],
                                ['2', 'Michael Jackson: The Experience', '506', '12', '8'],
                                [
                                    '3',
                                    'Clubhouse Games: 51 Worldwide Classics',
                                    '408',
                                    '87',
                                    '63',
                                ],
                                ['4', 'Climber Animals: Together', '370', '73', '60'],
                                ['5', 'Casino', '329', '120', '39'],
                                ['6', 'Maitre des Fleurs', '281', '54', '18'],
                                ['7', 'I Have an Item', '278', '21', '19'],
                                ['8', 'The Game of Life', '199', '17', '14'],
                                ['9', 'Who Wants to Be a Millionaire', '183', '13', '10'],
                                ['10', 'Gnaughty Gnomes', '180', '10', '9'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Super Battle Golf'),
                            text(' is the clear momentum story. After debuting in February, it has already leapt to #1 in March with 9,948 streamers, pushing '),
                            em('Among Us'),
                            text(' down to #2. Interestingly, while there was no changes in the top 10 from last month, '),
                            em('Diablo II: Resurrected'),
                            text(' dropped sharply with almost 2,000 less streamers captured during the full month of March compared to the second half of February (the reporting window for that month).'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'Super Battle Golf', '9,948', '14,132'],
                                ['2', 'Among Us', '8,744', '14,983'],
                                ['3', 'Fall Guys', '6,764', '14,345'],
                                ['4', 'Lethal Company', '4,463', '8,285'],
                                ['5', 'Super Smash Bros. Ultimate', '3,813', '13,515'],
                                ['6', 'Diablo II: Resurrected', '3,114', '20,276'],
                                ['7', 'Gartic Phone', '2,748', '4,780'],
                                ['8', 'Sonic Racing: CrossWorlds', '2,716', '6,067'],
                                ['9', 'Mario Kart World', '2,475', '6,345'],
                                ['10', 'Party Animals', '2,173', '3,899'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Words On Stream'),
                            text(' and '),
                            em('Ragnarok X: Next Generation'),
                            text(" remained the genre's clearest retention monsters, but this month's biggest stickiness development was "),
                            em('Diablo II: Resurrected'),
                            text(" rising from 4.8 to 6.5 which helped to compensate for the drop in streamer count. While the absolute number of streams won't knock your socks off, "),
                            em('Scrabble'),
                            text(', '),
                            em('Drawful 2'),
                            text(', and '),
                            em('Debate'),
                            text(' have streamers dedicated to playing these games multiple times per week on average.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'Ragnarok X: Next Generation', '12.7', '828', '65'],
                                ['2', 'Words On Stream', '11.9', '7,468', '626'],
                                ['3', 'Scrabble', '11.0', '242', '22'],
                                ['4', 'Drawful 2', '9.3', '232', '25'],
                                ['5', 'Debate', '7.5', '151', '20'],
                                ['6', 'Diablo II: Resurrected', '6.5', '20,276', '3,114'],
                                ['7', 'Gartic on Stream', '5.6', '218', '39'],
                                [
                                    '8',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '5.2',
                                    '215',
                                    '41',
                                ],
                                ['9', 'Mafia Online', '5.1', '172', '34'],
                                [
                                    '10',
                                    'The Legend of Heroes: Trails of Cold Steel II',
                                    '5.1',
                                    '143',
                                    '28',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            blocks: [
                p([
                    text('Forty-six party games launched in March 2026, and several found an audience on Twitch right away. '),
                    em('Poker Night at the Inventory'),
                    text(' was the headline debut, '),
                    em('Ham: The Game'),
                    text(' also got off to a promising start, and a handful of other releases found their way onto streams before the month was out. For a new party game, any early pickup is worth celebrating.'),
                ]),
            ],
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Poker Night at the Inventory'),
                            text(' had an excellent opening month, reaching 804 streamers and 1,114 observed streams almost immediately after launch. '),
                            em('Ham: The Game'),
                            text(' also arrived with encouraging early momentum, and '),
                            em('Gnaughty Gnomes'),
                            text(' showed that you can still attract a lot of eyeballs even with just a few streams if the right creator picks it up.'),
                        ]),
                        p('Every game in this table cleared the hardest hurdle a new release faces: getting streamers to try it at all. That first wave of creator curiosity is often what decides whether a game becomes a recurring Twitch pick or earns a second burst of attention later on.'),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    'Poker Night at the Inventory',
                                    'Mar 5',
                                    '1,114',
                                    '804',
                                    '26',
                                ],
                                ['Ham: The Game', 'Mar 3', '309', '266', '39'],
                                ['Beetleball', 'Mar 1', '28', '21', '4'],
                                ['Gnaughty Gnomes', 'Mar 3', '10', '9', '180'],
                                ['Roombattle', 'Mar 25', '5', '5', '22'],
                                ['The Artifactory', 'Mar 10', '4', '3', '24'],
                                ['Game with Friends', 'Mar 27', '2', '2', '2'],
                                ['Chained Beasts', 'Mar 31', '1', '1', '2'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('The remaining 38 party games launched in March without observed Twitch activity during the capture window. We list them below in release-date order in hopes of helping creators and viewers discover something new.'),
                        list({
                            items: [
                                'Mayday Protocol (Mar 2)',
                                'Blokes on Blocks! (Mar 4)',
                                'Velocide Overdrive (Mar 4)',
                                'Chicken Chicken (Mar 5)',
                                'HeadHunters (Mar 5)',
                                'Line Up! (Mar 5)',
                                'Pororo Sports Day (Mar 5)',
                                'Super Smash Bros. Ultimate: Planet Zebes Smash 64 Stage (Mar 5)',
                                'Valla (Mar 11)',
                                'Quick Brain Letter Hunt (Mar 12)',
                                'Quick Deduction Short Mysteries (Mar 12)',
                                'Versus One (Mar 12)',
                                'SketchCode (Mar 13)',
                                'Super Smash Bros. Ultimate: Iron Man Moveset (Mar 14)',
                                'Super Smash Bros. Ultimate: Akaza Moveset (Mar 15)',
                                'Super Smash Bros. Ultimate: Ichiban Kasuga Moveset (Yakuza) (Mar 15)',
                                'Super Smash Bros. Ultimate: Megumin Moveset (Mar 15)',
                                'Super Smash Bros. Ultimate: Paper Mario Moveset (Mar 15)',
                                'Dumpster Dive Live (Mar 16)',
                                'YEKO (Mar 16)',
                                'Bibimba wo Mazesaseruna 2 (Mar 17)',
                                'Avian Knights (Mar 19)',
                                'Maimai DX Circle Plus (Mar 19)',
                                'Quick Brain: Number Hunt (Mar 19)',
                                'Solitaire (Mar 19)',
                                'Cowboy Kart (Mar 20)',
                                'Jesus Take The Wheel (Mar 24)',
                                'Find the Funny Mistake! (Mar 26)',
                                'Peek a Rabbit! (Mar 26)',
                                'Sports Challenge: 50+ Games Edition (Mar 27)',
                                'Bun Buns (Mar 28)',
                                'Buckle Up (Mar 30)',
                                'DeGen Rivals (Mar 31)',
                                'Dino Party (Mar 31)',
                                'Frutiger Space (Mar 31)',
                                'Kaporanga (Mar 31)',
                                'SUMBR (Mar 31)',
                                'The Bold Club (Mar 31)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            blocks: [
                p('This section profiles streamers three ways: most active, biggest audiences, and most versatile. Each surfaces a different type of creator influence in the party game ecosystem.'),
            ],
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text("February's pattern held almost intact. Eight of March's top 10 most active channels were already in February's top 10, and the leaderboard was again dominated by single-game feeds that were effectively always on: "),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' advertised 24/7 Diablo II reruns, '),
                            link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                            text(' ran 24/7 Smash tournament matches, '),
                            link('https://twitch.tv/jackboxplay', 'jackboxplay'),
                            text(' ran a 24/7 Jackbox room, and Words On Stream channels like '),
                            link('https://twitch.tv/suzyqpid', 'suzyqpid'),
                            text(', '),
                            link('https://twitch.tv/thesptwitch', 'TheSpTwitch'),
                            text(', and '),
                            link('https://twitch.tv/tygerladi', 'TygerLadi'),
                            text(" appeared in more than 90% of the month's capture events. "),
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' was the standout because that near-constant Fall Guys presence also carried a real audience, averaging 299 viewers, while '),
                            link('https://twitch.tv/cpxtrey', 'CPxTrey'),
                            text(' was the only mixed-format community host.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '207',
                                    '102',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '206',
                                    '12',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '204',
                                    '299',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '204',
                                    '15',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '203',
                                    '33',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '196',
                                    '132',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '187',
                                    '28',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                    '164',
                                    '57',
                                    '3',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/mike_and_chill', 'Mike_and_Chill'),
                                    ],
                                    '146',
                                    '11',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/scufflestv', 'ScufflesTV')],
                                    '141',
                                    '21',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 captures',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(' stayed at #1, but the more interesting shift was how international and event-driven this table became. Only three names carried over from February, while the rest came from international channels that touched party games for just a few captures, usually around collabs or special sessions rather than steady category commitment. '),
                            em('Super Battle Golf'),
                            text(' was the clearest connective tissue, helping pull in '),
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(', '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', '),
                            link('https://twitch.tv/kamet0', 'Kamet0'),
                            text(', '),
                            link('https://twitch.tv/joueur_du_grenier', 'Joueur_du_Grenier'),
                            text(', and '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(", which makes March's audience table look less like a measure of party-game loyalty and more like a map of which games were best at turning a big creator's night into an event."),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Captures',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                    '54,596',
                                    '6',
                                    '1',
                                ],
                                [
                                    '2',
                                    [
                                        link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                    ],
                                    '39,960',
                                    '3',
                                    '2',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '34,568',
                                    '4',
                                    '2',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                    '30,281',
                                    '3',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/rubius', 'Rubius')],
                                    '26,615',
                                    '2',
                                    '2',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/xqc', 'xQc')],
                                    '25,646',
                                    '2',
                                    '2',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/sasavot', 'sasavot')],
                                    '23,432',
                                    '2',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/kamet0', 'Kamet0')],
                                    '13,406',
                                    '2',
                                    '2',
                                ],
                                [
                                    '9',
                                    [
                                        link('https://twitch.tv/joueur_du_grenier', 'Joueur_du_Grenier'),
                                    ],
                                    '11,950',
                                    '2',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/northernlion', 'Northernlion')],
                                    '10,032',
                                    '3',
                                    '3',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Versatility became more visible in March. '),
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' expanded from 13 games in February to 18 in March, while '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(' and '),
                            link('https://twitch.tv/attackingtucans', 'AttackingTucans'),
                            text(' entered as high-impact multi-game creators. Channels like '),
                            link('https://twitch.tv/ray', 'Ray'),
                            text(' brought large audiences to multiple games across the genre.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Captures',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '18',
                                    '80',
                                    '26',
                                ],
                                [
                                    '2',
                                    [
                                        link('https://twitch.tv/attackingtucans', 'AttackingTucans'),
                                    ],
                                    '14',
                                    '26',
                                    '23',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '14',
                                    '17',
                                    '203',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/yoshuago', 'YoshuaGo')],
                                    '11',
                                    '16',
                                    '19',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/quarrysea', 'QuarrySea')],
                                    '9',
                                    '22',
                                    '50',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/zacoboyfm', 'ZacoboyFM')],
                                    '9',
                                    '14',
                                    '18',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/super_cubby', 'super_cubby')],
                                    '8',
                                    '16',
                                    '46',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/snapfingerclick', 'SnapFingerClick'),
                                    ],
                                    '8',
                                    '12',
                                    '14',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/ray', 'Ray')],
                                    '8',
                                    '11',
                                    '2,317',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/wardere', 'WARDERE')],
                                    '7',
                                    '62',
                                    '32',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            blocks: [
                p('Party game streaming on Twitch spanned 35 languages in March, with Vietnamese appearing as a new language in the party game scene.'),
            ],
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('English still led comfortably by volume with Spanish holding steady at #2. The share of English streams ticked up slightly from last month, but average viewership dropped slightly. Italian posted the highest average viewers per stream by a wide margin, led by '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(' streaming '),
                            em('Super Battle Golf'),
                            text('. Chinese and German stayed strong on the same measure, while Polish viewership cooled off from last month.'),
                        ]),
                        p([
                            text('The per-language breakdowns below show that these communities are not just translating the same tastes. '),
                            em('Mahjong'),
                            text(' remained central in Japanese and Chinese streams, Russian channels kept leaning into social and deduction-heavy games, and '),
                            em('Fall Guys'),
                            text(' continued to travel especially well across Spanish, Portuguese, and Italian creators.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '133,344', '51,691', '19'],
                                ['2', 'Spanish', '15,206', '7,160', '17'],
                                ['3', 'German', '10,876', '4,612', '38'],
                                ['4', 'French', '9,883', '4,108', '29'],
                                ['5', 'Russian', '9,027', '3,584', '30'],
                                ['6', 'Japanese', '8,645', '3,239', '19'],
                                ['7', 'Portuguese', '4,654', '2,179', '30'],
                                ['8', 'Chinese', '3,771', '1,399', '41'],
                                ['9', 'Italian', '1,442', '637', '88'],
                                ['10', 'Polish', '1,390', '562', '44'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '12,635', '1,996'],
                                        ['2', 'Super Battle Golf', '12,017', '8,373'],
                                        ['3', 'Super Smash Bros. Ultimate', '9,178', '2,508'],
                                        ['4', 'Fall Guys', '8,519', '4,198'],
                                        ['5', 'Among Us', '8,456', '4,785'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        [
                                            '1',
                                            'Clubhouse Games: 51 Worldwide Classics',
                                            '976',
                                            '32',
                                        ],
                                        ['2', 'Michael Jackson: The Experience', '551', '11'],
                                        ['3', 'Casino', '383', '58'],
                                        ['4', 'SLAMPUNKS', '242', '17'],
                                        ['5', 'Who Wants to Be a Millionaire', '206', '11'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '213',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '207',
                                            '102',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/visionz44', 'Visionz44')],
                                            '207',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '206',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '204',
                                            '15',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '54,596',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                            ],
                                            '39,960',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/xqc', 'xQc')],
                                            '25,646',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '10,032',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/realzbluewater', 'RealzBlueWater'),
                                            ],
                                            '5,902',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-2',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '1,854', '953'],
                                        ['2', 'Among Us', '1,772', '1,126'],
                                        ['3', 'Super Smash Bros. Ultimate', '1,610', '555'],
                                        ['4', 'Mario Kart World', '940', '303'],
                                        ['5', 'Gartic Phone', '794', '473'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-2',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Wii Sports', '190', '23'],
                                        ['2', 'Nintendo Switch Sports', '170', '12'],
                                        ['3', 'Just Dance 2026 Edition', '164', '76'],
                                        ['4', 'LEGO Party!', '162', '11'],
                                        ['5', 'Just Dance', '44', '12'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-2',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/ravenslgaming', 'ravenslgaming'),
                                            ],
                                            '51',
                                            '14',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/do0mhead', 'do0mhead')],
                                            '49',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrcarpatv', 'MrCarpaTV')],
                                            '47',
                                            '163',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/redhdrake', 'RedHDraKe')],
                                            '31',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/venconbob', 'VenconBOB')],
                                            '29',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-2',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/rubius', 'Rubius')],
                                            '26,615',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/agustinunaplay8', 'AgustinUnaplay8'),
                                            ],
                                            '4,182',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,405',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mictia00', 'Mictia00')],
                                            '2,079',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/elded', 'elded')],
                                            '2,022',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-3',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,923', '1,049'],
                                        ['2', 'Diablo II: Resurrected', '1,464', '224'],
                                        ['3', 'Fall Guys', '994', '447'],
                                        ['4', 'Mario Kart 8 Deluxe', '594', '263'],
                                        ['5', 'Super Smash Bros. Ultimate', '553', '162'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-3',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The House Always Wins', '615', '10'],
                                        ['2', 'Golf With Your Friends', '198', '478'],
                                        ['3', 'Job Simulator: The 2050 Archives', '131', '6'],
                                        ['4', 'Codenames', '104', '228'],
                                        ['5', 'Gartic Phone', '97', '197'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-3',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/meloi_tv', 'Meloi_TV')],
                                            '51',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kastell1974', 'Kastell1974')],
                                            '50',
                                            '47',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/schwauts', 'Schwauts')],
                                            '46',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/toom4s', 'toom4s')],
                                            '44',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/smashcuzz', 'SmashCuzz')],
                                            '39',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-3',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '34,568',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bastighg', 'BastiGHG')],
                                            '6,312',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '4,479',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                                            ],
                                            '3,874',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/alibumayetv', 'AliBumayeTV')],
                                            '965',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-4',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,145', '675'],
                                        ['2', 'Fall Guys', '1,019', '435'],
                                        ['3', 'Mario Kart World', '897', '302'],
                                        ['4', 'Super Smash Bros. Ultimate', '820', '265'],
                                        ['5', 'Diablo II: Resurrected', '724', '117'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-4',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Maitre des Fleurs', '281', '54'],
                                        ['2', 'Super Battle Golf', '204', '374'],
                                        ['3', 'Stream Pairs', '144', '15'],
                                        [
                                            '4',
                                            'Super Mario Party Jamboree: Nintendo Switch 2 Edition + Jamboree TV',
                                            '123',
                                            '8',
                                        ],
                                        ['5', 'Lycans', '106', '303'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-4',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '190',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/barbie_the_queen', 'barbie_the_queen'),
                                            ],
                                            '72',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nayru', 'Nayru')],
                                            '54',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tomka02', 'TOMKA02')],
                                            '34',
                                            '15',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ss7even', 'Ss7even')],
                                            '33',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-4',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/kamet0', 'Kamet0')],
                                            '13,406',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/joueur_du_grenier', 'Joueur_du_Grenier'),
                                            ],
                                            '11,950',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,962',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/hortyunderscore', 'HortyUnderscore'),
                                            ],
                                            '3,399',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/fugu_fps', 'fugu_fps')],
                                            '1,578',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-5',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '1,558', '206'],
                                        ['2', 'Gartic Phone', '1,364', '674'],
                                        ['3', 'Lethal Company', '890', '307'],
                                        ['4', 'Jackbox Party Packs', '679', '244'],
                                        ['5', 'SIGame', '487', '351'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-5',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Climber Animals: Together', '822', '32'],
                                        ['2', 'Casino', '667', '25'],
                                        ['3', 'Dale & Dawson Stationery Supplies', '495', '53'],
                                        ['4', 'UNO', '305', '9'],
                                        ['5', 'Super Battle Golf', '134', '196'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-5',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '196',
                                            '132',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kasto999', 'Kasto999')],
                                            '66',
                                            '39',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/sashatechnic', 'SashaTechnic'),
                                            ],
                                            '62',
                                            '24',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/migellitta', 'Migellitta')],
                                            '54',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kos_jv', 'Kos_JV')],
                                            '51',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-5',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sasavot', 'sasavot')],
                                            '23,432',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/olesha', 'Olesha')],
                                            '2,654',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '2,450',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/saylowzol', 'SAYLOWZOL')],
                                            '1,598',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vika_karter', 'vika_karter')],
                                            '1,560',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-6',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,905', '671'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,086', '235'],
                                        ['3', 'Beat Saber', '717', '161'],
                                        ['4', 'Mario Kart World', '467', '125'],
                                        ['5', 'Fall Guys', '410', '151'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-6',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Placid Plastic Duck Simulator', '152', '26'],
                                        ['2', 'Goose Goose Duck', '110', '157'],
                                        ['3', 'Paddle Paddle Paddle', '87', '35'],
                                        ['4', 'Super Battle Golf', '80', '244'],
                                        ['5', 'The You Quiz', '77', '7'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-6',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/knobara', '釘崎くぎみん')],
                                            '47',
                                            '50',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sufugamofu', 'スフガモフ_')],
                                            '47',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sabomkw', 'さっぼさん')],
                                            '39',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '37',
                                            '180',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/shiratama__3', 'しらたまぁああ'),
                                            ],
                                            '37',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-6',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '6,571',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/mitsuki_mitsuu', 'みつきさんです'),
                                            ],
                                            '1,877',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/shuto_fgc', 'shuto_fgc')],
                                            '1,452',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/rio_taro', 'Rio_taro')],
                                            '902',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/murakamisuigun', 'むらまこ')],
                                            '690',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-7',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '510', '171'],
                                        ['2', 'Diablo II: Resurrected', '335', '80'],
                                        ['3', 'Among Us', '262', '188'],
                                        ['4', 'Ragnarok X: Next Generation', '256', '12'],
                                        ['5', 'Stumble Guys', '222', '94'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-7',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'I Have an Item', '641', '9'],
                                        ['2', 'Fall Guys', '126', '510'],
                                        ['3', 'Gartic on Stream', '68', '99'],
                                        ['4', "Liar's Bar", '66', '55'],
                                        ['5', 'Unrailed!', '60', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-7',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '204',
                                            '299',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/boozeiira', 'Boozeiira')],
                                            '140',
                                            '22',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/alphonserox', 'alphonserox')],
                                            '49',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tiowillshow', 'TioWillShow')],
                                            '47',
                                            '19',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/choke7', 'Choke7')],
                                            '45',
                                            '79',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-7',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mount', 'mount')],
                                            '2,474',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sabrinoca', 'sabrinoca')],
                                            '716',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '612',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/veronikavolt', 'VeronikaVolt'),
                                            ],
                                            '584',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tftoddy', 'TFToddy')],
                                            '356',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-8',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '737', '270'],
                                        ['2', 'Diablo II: Resurrected', '729', '86'],
                                        ['3', 'DisasterPR+', '481', '394'],
                                        ['4', 'Super Battle Golf', '228', '193'],
                                        ['5', 'Mabinogi', '197', '35'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-8',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Jinsei Game for Nintendo Switch', '1,109', '14'],
                                        ['2', 'Super Battle Golf', '112', '228'],
                                        ['3', 'Mahjong Soul', '89', '737'],
                                        ['4', 'Goose Goose Duck', '67', '166'],
                                        ['5', 'Climber Animals: Together', '64', '8'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-8',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/slmtenno2025', '史萊姆的第二個家'),
                                            ],
                                            '103',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/purplecat97803', '紫の喵')],
                                            '62',
                                            '40',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/karis835', 'karis835')],
                                            '39',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/raven_ouo', '渡夜爵')],
                                            '36',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/spark3388', '_鵝鵝_')],
                                            '32',
                                            '11',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-8',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '3,665',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bbbb87', '世誠')],
                                            '2,731',
                                            '11',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/earendelxdfp', '厄倫蒂兒')],
                                            '2,039',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/restiafps', 'restiafps')],
                                            '1,732',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/greentea', '綠茶')],
                                            '1,698',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-9',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '259', '113'],
                                        ['2', 'Among Us', '157', '89'],
                                        ['3', 'Super Smash Bros. Ultimate', '111', '33'],
                                        ['4', 'Mario Kart World', '74', '36'],
                                        ['5', 'Gartic Phone', '73', '54'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-9',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '2,196', '48'],
                                        ['2', 'Wii Party', '114', '12'],
                                        ['3', 'Gartic Show', '80', '13'],
                                        ['4', 'Live Bingo', '37', '9'],
                                        ['5', 'Mario Kart 8 Deluxe', '34', '31'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-9',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/nonnogamer70', 'NonnoGamer70'),
                                            ],
                                            '23',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '19',
                                            '24',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '18',
                                            '154',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/lidiass3', 'lidiass3')],
                                            '15',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/exandertv', 'exandertv')],
                                            '12',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-9',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                            '30,281',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '3,036',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/giankoextreme', 'giankoextreme'),
                                            ],
                                            '1,072',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/itermosifoni', 'iTermosifoni'),
                                            ],
                                            '404',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/pesh', 'Pesh')],
                                            '391',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    blocks: [],
                    sections: [
                        section({
                            id: 'language-landscape__games-with-most-streams-10',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '530', '83'],
                                        ['2', 'Among Us', '148', '96'],
                                        ['3', 'Fall Guys', '116', '28'],
                                        ['4', 'Lethal Company', '68', '39'],
                                        ['5', 'Gartic Phone', '54', '43'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__games-with-biggest-audiences-10',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Among Us', '267', '148'],
                                        ['2', 'HAM: The Game', '76', '8'],
                                        ['3', 'Whatsamusic', '65', '15'],
                                        ['4', 'Super Battle Golf', '28', '16'],
                                        ['5', 'Stumble Guys', '25', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-most-activity-10',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '75',
                                            '138',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '24',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/karmazynowyy_', 'karmazynowyy_'),
                                            ],
                                            '17',
                                            '13',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/iergo', 'iErGO')],
                                            '14',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/czechoooos', 'czechoooos')],
                                            '11',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__streamers-with-biggest-audiences-10',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ewroon', 'EWROON')],
                                            '7,573',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kasix', 'Kasix')],
                                            '1,331',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '1,296',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bladii309', 'Bladii309')],
                                            '351',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mrs_nocka', 'mrs_NOCKA')],
                                            '144',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            blocks: [
                p('These deep dives zoom in on the top 10 Game Party Score titles by showing who streamed them most often and who drew the largest audiences around them. Because Airhead Games relies on IGDB and Twitch category structure, some entries stretch the conventional idea of a party game, but that classification is part of the market reality streamers and viewers actually encounter.'),
            ],
            sections: [
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('20,276 streams | 3,114 streamers | 6.5 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '207',
                                            '102',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/alexislegit', 'AlexIsLegit')],
                                            '131',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/slmtenno2025', '史萊姆的第二個家'),
                                            ],
                                            '103',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '94',
                                            '30',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/alvinx1991', 'alvinx1991')],
                                            '88',
                                            '20',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/2dkiri', '2DKiri')],
                                            '2,130',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/guzu', 'Guzu')],
                                            '1,269',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/datmodz', 'DatModz')],
                                            '1,229',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/avoidingthepuddle', 'AvoidingThePuddle'),
                                            ],
                                            '1,205',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '947',
                                            '46',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('14,345 streams | 6,764 streamers | 2.1 stickiness | 43 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-2',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '204',
                                            '299',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/hexagone____', 'Hexagone____'),
                                            ],
                                            '56',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/weedmanyogurt24', 'weedmanyogurt24'),
                                            ],
                                            '51',
                                            '16',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/snoochboochy', 'snoochboochy'),
                                            ],
                                            '49',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xternalkiwi', 'xternalkiwi')],
                                            '45',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-2',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '54,596',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/realzbluewater', 'RealzBlueWater'),
                                            ],
                                            '5,902',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,065',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dorozea', 'dorozea')],
                                            '781',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aplatypuss', 'APlatypuss')],
                                            '656',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('3,508 streams | 1,235 streamers | 2.8 stickiness | 29 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-3',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/knobara', '釘崎くぎみん')],
                                            '47',
                                            '50',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sufugamofu', 'スフガモフ_')],
                                            '47',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '46',
                                            '28',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/gelnarx', 'Gelnarx')],
                                            '41',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/shiratama__3', 'しらたまぁああ'),
                                            ],
                                            '37',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-3',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bbbb87', '世誠')],
                                            '2,731',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/greentea', '綠茶')],
                                            '1,698',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/liang_0714', '小亮')],
                                            '1,570',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/umidan1207', '貓糰')],
                                            '636',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/yulihong22', '咪咪蛋')],
                                            '397',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,468 streams | 626 streamers | 11.9 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-4',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '206',
                                            '12',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '203',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '187',
                                            '28',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '177',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kodabunz', 'kodabunz')],
                                            '111',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-4',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '606',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/skinnedteen', 'skinnedteen')],
                                            '283',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/emilycc', 'emilycc')],
                                            '234',
                                            '109',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/couriway', 'Couriway')],
                                            '209',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sreme', 'sreme')],
                                            '208',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('444 streams | 177 streamers | 2.5 stickiness | 63 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-5',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/summerskin90', 'summerskin90'),
                                            ],
                                            '16',
                                            '11',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ratatoscr', 'RATATOscr')],
                                            '14',
                                            '24',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/floridawoman', 'floridawoman'),
                                            ],
                                            '13',
                                            '33',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '13',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/sierravampire', 'SierraVampire'),
                                            ],
                                            '11',
                                            '67',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-5',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,405',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,385',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/littlesiha', 'littlesiha')],
                                            '492',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/sincerelylyn', 'sincerelyLyn'),
                                            ],
                                            '270',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '199',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('709 streams | 165 streamers | 4.3 stickiness | 32 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-6',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/laser_los', 'laser_los')],
                                            '23',
                                            '38',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/illawarrapinball', 'illawarrapinball'),
                                            ],
                                            '21',
                                            '35',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/carpoolpinball', 'CarpoolPinball'),
                                            ],
                                            '16',
                                            '68',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/whatsbrewing', 'whatsbrewing'),
                                            ],
                                            '16',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '15',
                                            '400',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-6',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/aimlesspinball', 'AimlessPinball'),
                                            ],
                                            '474',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '400',
                                            '15',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/lyonsclassicpinball', 'LyonsClassicPinball'),
                                            ],
                                            '277',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/carpoolpinball', 'CarpoolPinball'),
                                            ],
                                            '68',
                                            '16',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ypsipinball', 'YpsiPinball')],
                                            '58',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__lycans',
                    title: 'Lycans',
                    collapsible: true,
                    blocks: [
                        p('312 streams | 122 streamers | 2.6 stickiness | 104 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-7',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/ressapandasan', 'RessaPandaSan'),
                                            ],
                                            '21',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hornicoo', 'HorNicoo')],
                                            '18',
                                            '27',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/malis60', 'malis60')],
                                            '8',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/blue_nurse', 'blue_nurse')],
                                            '7',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tsunadida', 'Tsunadida')],
                                            '6',
                                            '131',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-7',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '4,611',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/anaee', 'Anaee')],
                                            '1,066',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lutti', 'Lutti')],
                                            '429',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/romainjacques_', 'RomainJacques_'),
                                            ],
                                            '376',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/bytell2', 'Bytell2')],
                                            '284',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('13,515 streams | 3,813 streamers | 3.5 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-8',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '213',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '204',
                                            '15',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/afk_host', 'AFK_Host')],
                                            '197',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mew2king247', 'Mew2King247')],
                                            '65',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/superdumbnasty', 'SuperDumbNasty'),
                                            ],
                                            '46',
                                            '22',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-8',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '6,571',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sakor_', 'Sakor_')],
                                            '1,113',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/murakamisuigun', 'むらまこ')],
                                            '690',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '654',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/chez_ib', 'chez_ib')],
                                            '413',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-64',
                    title: 'Mario Kart 64',
                    collapsible: true,
                    blocks: [
                        p('386 streams | 129 streamers | 3.0 stickiness | 40 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-9',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '30',
                                            '356',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/wrechman_', 'wrechman_')],
                                            '30',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/grey0__', 'grey0__')],
                                            '19',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '17',
                                            '67',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/katze789', 'katze789')],
                                            '10',
                                            '25',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-9',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '356',
                                            '30',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '67',
                                            '17',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/brodymk64', 'BrodyMK64')],
                                            '56',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/deadhamster35', 'DeadHamster35'),
                                            ],
                                            '47',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tylerscheid', 'TylerScheid')],
                                            '33',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__gartic-on-stream',
                    title: 'Gartic on Stream',
                    collapsible: true,
                    blocks: [
                        p('218 streams | 39 streamers | 5.6 stickiness | 57 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__streamers-with-most-activity-10',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                            '73',
                                            '57',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ysseneg', 'ysseneg')],
                                            '34',
                                            '119',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/choke7', 'Choke7')],
                                            '26',
                                            '86',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tadashii_eu', 'tadashii_eu')],
                                            '22',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/camnutz', 'Camnutz')],
                                            '7',
                                            '80',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__streamers-with-biggest-audiences-10',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ysseneg', 'ysseneg')],
                                            '119',
                                            '34',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/choke7', 'Choke7')],
                                            '86',
                                            '26',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/camnutz', 'Camnutz')],
                                            '80',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                            '57',
                                            '73',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/potatopcti_super', 'PotatoPcTi_Super'),
                                            ],
                                            '39',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            blocks: [
                p('The below data is based on 207 capture events throughout March 2026.'),
            ],
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p('A clearer trend has emerged this month in streaming patterns by day of week. Monday through Thursday all see approximately the same number of streams but, as the week progresses towards the weekend, the average viewership starts to creep up with a noticeable peak on Thursday. All else equal, this translates to more than 10,000 additional viewers watching party game content on Thursdays compared to earlier in the week. Streamers looking to maximize their viewership may want to consider adding a Wednesday or Thursday stream to their schedule.'),
                        p('Once the weekend rolls around, there is a big spike in the total number of party game streams between Friday and Sunday. With the increased volume, viewership per stream dips noticeably; possibly because of competing content or would-be viewers are streaming themselves instead.'),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,203', '21'],
                                ['Monday', '859', '22'],
                                ['Tuesday', '846', '23'],
                                ['Wednesday', '858', '27'],
                                ['Thursday', '867', '35'],
                                ['Friday', '1,000', '17'],
                                ['Saturday', '1,271', '15'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p("The busiest hours for streams run from about 1:00 to 3:00 UTC, which lines up with the evening hours in the United States; however, average viewership peaks earlier on the clock around 22:00 UTC. At that time the United States is in late afternoon and early evening when people are winding down from school and work, while Western Europe is in prime time. No second peak aligns with Asian or Australian evening; that UTC band is also the quietest stretch of the day for streams, indicating a bias towards the Atlantic for Twitch's overall audience."),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,414', '14'],
                                ['01:00', '1,533', '20'],
                                ['02:00', '1,577', '20'],
                                ['03:00', '1,603', '23'],
                                ['04:00', '1,272', '30'],
                                ['05:00', '985', '19'],
                                ['06:00', '933', '13'],
                                ['07:00', '638', '26'],
                                ['08:00', '457', '16'],
                                ['09:00', '439', '18'],
                                ['10:00', '461', '18'],
                                ['11:00', '456', '21'],
                                ['12:00', '539', '23'],
                                ['13:00', '708', '18'],
                                ['14:00', '750', '23'],
                                ['15:00', '708', '18'],
                                ['16:00', '820', '24'],
                                ['17:00', '957', '22'],
                                ['18:00', '1,028', '20'],
                                ['19:00', '1,181', '17'],
                                ['20:00', '1,395', '24'],
                                ['21:00', '1,314', '26'],
                                ['22:00', '1,288', '34'],
                                ['23:00', '1,300', '25'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-03-top-party-games-and-streamers.js.map